import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Wrench, FileText, Download, Trash2, Activity, CheckCircle2, XCircle, Ban, ShieldAlert } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function StatusDot({ tool }) {
  if (!tool.configured) return <span className="wr-mono text-[0.6rem] uppercase text-wr-muted border border-wr-border px-1.5 py-0.5" data-testid={`tool-status-${tool.id}`}>Not configured</span>;
  if (!tool.enabled) return <span className="wr-mono text-[0.6rem] uppercase text-amber-400 border border-amber-500/40 px-1.5 py-0.5" data-testid={`tool-status-${tool.id}`}>Disabled</span>;
  return <span className="wr-mono text-[0.6rem] uppercase text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5" data-testid={`tool-status-${tool.id}`}>Available</span>;
}

function RunIcon({ status }) {
  if (status === "success") return <CheckCircle2 size={13} className="text-emerald-400" />;
  if (status === "failed") return <XCircle size={13} className="text-wr-red" />;
  if (status === "blocked") return <Ban size={13} className="text-amber-400" />;
  return <Activity size={13} className="text-wr-muted" />;
}

export default function FilesTools() {
  const [tools, setTools] = useState([]);
  const [files, setFiles] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [runs, setRuns] = useState([]);
  const [usage, setUsage] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, f, a, r, u] = await Promise.all([
        api.get("/tools"), api.get("/tools/files"), api.get("/tools/artifacts"),
        api.get("/tools/runs"), api.get("/tools/usage"),
      ]);
      setTools(t.data.tools);
      setFiles(f.data);
      setArtifacts(a.data);
      setRuns(r.data);
      setUsage(u.data);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = (kind, id, filename) => {
    const url = `${BACKEND}/api/tools/${kind}/${id}/download`;
    const a = document.createElement("a");
    a.href = url; a.download = filename || ""; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const delFile = async (id) => {
    try { await api.delete(`/tools/files/${id}`); toast.success("File deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delArtifact = async (id) => {
    try { await api.delete(`/tools/artifacts/${id}`); toast.success("Artifact deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-8" data-testid="files-tools-page">
      <p className="wr-label !text-wr-red">▸ SARGE · FILES & TOOLS</p>
      <h1 className="wr-heading font-bold text-white text-2xl sm:text-3xl mt-2">Your tools & documents.</h1>
      <p className="text-sm text-wr-sub mt-2 max-w-xl">Everything Sarge can do, the files you've shared, and the artifacts he's produced — all scoped to your workspace.</p>

      {/* Tools */}
      <section className="mt-7">
        <h2 className="wr-label flex items-center gap-2"><Wrench size={14} className="text-wr-red" /> Available tools</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tools-grid">
          {tools.filter((t) => t.configured).map((t) => (
            <div key={t.id} className="border border-wr-border bg-wr-surface p-4" data-testid={`tool-card-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-white font-semibold">{t.name}</p>
                <StatusDot tool={t} />
              </div>
              <p className="text-xs text-wr-sub mt-1.5 leading-relaxed">{t.description}</p>
              {t.status_reason && !t.enabled && (
                <p className="wr-mono text-[0.62rem] text-wr-muted mt-2 flex items-start gap-1.5"><ShieldAlert size={11} className="mt-0.5 shrink-0" /> {t.status_reason}</p>
              )}
              <p className="wr-mono text-[0.58rem] text-wr-muted mt-2 uppercase tracking-wider">v{t.version} · {t.executor}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Files */}
      <section className="mt-8">
        <h2 className="wr-label flex items-center gap-2"><FileText size={14} className="text-wr-red" /> Uploaded files ({files.length})</h2>
        <div className="mt-3 space-y-2" data-testid="files-list">
          {files.length === 0 && <p className="text-sm text-wr-muted border border-wr-border bg-wr-surface p-4">No files uploaded yet.</p>}
          {files.map((f) => (
            <div key={f.id} className="border border-wr-border bg-wr-surface p-3 flex items-center justify-between gap-3" data-testid={`file-row-${f.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{f.filename}</p>
                <p className="wr-mono text-[0.62rem] text-wr-muted">{f.kind?.toUpperCase()} · {(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleDateString()} {f.readable ? "" : "· unreadable"}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => download("files", f.id, f.filename)} data-testid={`file-download-${f.id}`} className="px-2.5 py-1.5 border border-wr-border text-wr-sub hover:text-white"><Download size={13} /></button>
                <button onClick={() => delFile(f.id)} data-testid={`file-delete-${f.id}`} className="px-2.5 py-1.5 border border-wr-border text-wr-sub hover:text-wr-red hover:border-wr-red"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Artifacts */}
      <section className="mt-8">
        <h2 className="wr-label flex items-center gap-2"><FileText size={14} className="text-wr-red" /> Generated artifacts ({artifacts.length})</h2>
        <div className="mt-3 space-y-2" data-testid="artifacts-list">
          {artifacts.length === 0 && <p className="text-sm text-wr-muted border border-wr-border bg-wr-surface p-4">No artifacts generated yet.</p>}
          {artifacts.map((a) => (
            <div key={a.id} className="border border-wr-border bg-wr-surface p-3 flex items-center justify-between gap-3" data-testid={`artifact-row-${a.id}`}>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{a.filename}</p>
                <p className="wr-mono text-[0.62rem] text-wr-muted">{a.format?.toUpperCase()} · {(a.size / 1024).toFixed(0)} KB · {new Date(a.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => download("artifacts", a.id, a.filename)} data-testid={`artifact-download-${a.id}`} className="px-2.5 py-1.5 border border-wr-border text-wr-sub hover:text-white"><Download size={13} /></button>
                <button onClick={() => delArtifact(a.id)} data-testid={`artifact-delete-${a.id}`} className="px-2.5 py-1.5 border border-wr-border text-wr-sub hover:text-wr-red hover:border-wr-red"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Activity */}
      <section className="mt-8">
        <h2 className="wr-label flex items-center gap-2"><Activity size={14} className="text-wr-red" /> Recent tool activity</h2>
        {usage && (
          <p className="wr-mono text-[0.66rem] text-wr-muted mt-2" data-testid="tools-usage">
            {usage.total_runs} runs · {usage.files} files · {usage.artifacts} artifacts
          </p>
        )}
        <div className="mt-3 space-y-1.5" data-testid="runs-list">
          {runs.length === 0 && <p className="text-sm text-wr-muted">No activity yet.</p>}
          {runs.slice(0, 25).map((r) => (
            <div key={r.id} className="flex items-center gap-3 text-sm border-b border-wr-border py-1.5" data-testid={`run-${r.id}`}>
              <RunIcon status={r.status} />
              <span className="text-white">{r.tool_id}</span>
              <span className="wr-mono text-[0.62rem] text-wr-muted uppercase">{r.status}</span>
              {r.error && <span className="wr-mono text-[0.62rem] text-wr-red truncate">{r.error}</span>}
              <span className="wr-mono text-[0.62rem] text-wr-muted ml-auto">{new Date(r.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
